<template>
  <div class="projet-part">
    <h2 id="site">{{ $t("projet.site.titre") }}</h2>

    <RadioWithInputAndValidation
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      rules="required"
      :data="natures"
      :option-labels="natureLabels"
      :model-value="projetAnnuel.natureAireEducative"
      :vertical-display="true"
      :disabled="readonly || projetAnnuel.statut !== 'BROUILLON'"
      :label="$t('projet.inscription.site-inscription.nature-question')"
      :input-notice="
        projetAnnuel.etat == 'EN_COURS_DE_CREATION'
          ? $t('projet.inscription.site-inscription.nature-question-notice')
          : ''
      "
      vid="site_1"
      @update:model-value="updateNatureaireeducative"
      @manual-change="$emit('dirtify-project')"
    />
    <RadioWithInputAndValidation
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :data="natureSiteValue"
      :option-labels="natureSiteValueLabels"
      :model-value="projetAnnuel.natureSite"
      :disabled="readonly"
      :vertical-display="true"
      :label="$t('projetPage.infoAdministratives.site.nature-site')"
      :input-notice="$t('projetPage.infoAdministratives.site.nature-site-message')"
      :tooltip="$t('projetPage.infoAdministratives.site.nature-site-tooltip')"
      vid="site_natureSite"
      @update:model-value="updateNaturesite"
      @manual-change="modified"
    />
    <RadioWithInputAndValidation
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
      :data="proprietaireValue"
      :option-labels="proprietaireValueLabels"
      :model-value="projetAnnuel.autorisationProprietaire"
      :disabled="readonly"
      :vertical-display="true"
      :label="$t('projetPage.infoAdministratives.site.autorisation-proprietaire')"
      :input-notice="
        $t('projetPage.infoAdministratives.site.autorisation-proprietaire-input-notice')
      "
      vid="site_autorisationProprietaireSite"
      @update:model-value="updateAutorisationproprietaire"
      @manual-change="modified"
    />
    <RadioWithInputAndValidation
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :data="booleanValue"
      :option-labels="booleanValueLabels"
      :model-value="projetAnnuel.avisFavorableConseilMunicipalSite"
      :disabled="readonly"
      :vertical-display="true"
      :label="$t('projetPage.infoAdministratives.site.avis-favorable-conseil-municipal')"
      :input-notice="$t('projetPage.infoAdministratives.site.avis-favorable-input-notice')"
      :tooltip="$t('projetPage.infoAdministratives.site.autorisation-proprietaire-tooltip')"
      vid="site_avisFavorableConseilMunicipalSite"
      @update:model-value="updateAvisfavorableconseilmunicipalsite"
      @manual-change="modified"
    />
  </div>
</template>

<script lang="ts">
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import { DocLink } from "@/views/commons/DocumentationLinks.vue"
import { defineComponent } from "vue"
import RadioWithInputAndValidation from "@/views/inputs/RadioWithInputAndValidation.vue"
import { EnumHelper } from "@/model/bean/EnumHelper"

export default defineComponent({
  name: "ProjetSiteView",
  components: {
    RadioWithInputAndValidation,
  },
  props: {
    projetAnnuel: {
      type: Object as () => ProjetAnnuelDTO,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["dirtify-project", "projet-annuel-updated", "trigger-validation"],
  data() {
    return {
      booleanValue: [true, false],
      proprietaireValue: EnumHelper.allProprietaires,
      natures: EnumHelper.allNatures,
      natureSiteValue: EnumHelper.allNaturesSite,
      links: [] as DocLink[],
    }
  },
  computed: {
    booleanValueLabels(): string[] {
      return this.booleanValue.map((value) => {
        return this.$t(`${value}`).toString()
      })
    },

    proprietaireValueLabels(): string[] {
      return this.proprietaireValue.map((proprietaire) => {
        return this.$t(
          `projetPage.infoAdministratives.site.autorisation-proprietaire-labels.${proprietaire}`,
        ).toString()
      })
    },

    natureLabels(): string[] {
      return this.natures.map((nature) => {
        return this.$t(`projet.inscription.site-inscription.nature-labels.${nature}`).toString()
      })
    },

    natureSiteValueLabels(): string[] {
      return this.natureSiteValue.map((value) => {
        return this.$t(`projetPage.infoAdministratives.site.natureSite.${value}`).toString()
      })
    },
  },
  created() {
    this.links.push(
      new DocLink(
        "projet.doc.mise-disposition-terrain-url",
        "projet.doc.mise-disposition-terrain-label",
      ),
    )
  },
  methods: {
    updateAvisfavorableconseilmunicipalsite(newValue: any): void {
      this.updateProjetAnnuel({
        ...this.projetAnnuel,
        avisFavorableConseilMunicipalSite: newValue,
      })
    },
    updateAutorisationproprietaire(newValue: any): void {
      this.updateProjetAnnuel({
        ...this.projetAnnuel,
        autorisationProprietaire: newValue,
      })
    },
    updateNaturesite(newValue: any): void {
      this.updateProjetAnnuel({
        ...this.projetAnnuel,
        natureSite: newValue,
      })
    },
    updateNatureaireeducative(newValue: any): void {
      this.updateProjetAnnuel({
        ...this.projetAnnuel,
        natureAireEducative: newValue,
      })
    },
    modified(): void {
      this.$emit("dirtify-project")
    },
    updateProjetAnnuel(updatedProjetAnnuel: ProjetAnnuelDTO): void {
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      this.$emit("dirtify-project")
      this.$nextTick(() => this.$emit("trigger-validation"))
    },
  },
})
</script>

<style lang="scss" scoped></style>
